import React, { useMemo } from 'react';
import { ImageElement, TemplateElement, VideoElement } from '@bynder-studio/render-core';
import { Button, Inspector, List, Thumbnail, token } from '@bynder/design-system';
import { IconBuildShape, IconSync, IconTextField, IconMusicLibrary } from '@bynder/icons';
import { TextElement } from 'packages/pages/design/components/TextElement';
import { useShotItem } from 'packages/pages/design/sidebar/shots/useShotItem';
import MediaElement from 'packages/pages/design/components/MediaElement';
import ShapeElement from 'packages/pages/design/components/ShapeElement';
import useContentPreviewUrl from 'packages/hooks/useContentPreviewUrl';
import useObjectUrl from 'packages/hooks/useObjectUrl';
import { ShotElementType } from 'packages/pages/design/sidebar/types';
import useVariations from '../../hooks/useVariations';
import useDesign from '../../hooks/useDesign';

type Props = {
    isLast: boolean;
    shotElement: TemplateElement;
    isToolbarDisabled: boolean;
    isLoading: boolean;
    isDisabledByReview: boolean;
};

const ShotItem = ({ isLast, shotElement, isToolbarDisabled, isLoading, isDisabledByReview }: Props) => {
    const {
        id,
        type,
        isTextElement,
        isImageElement,
        isShapeElement,
        isAudioElement,
        isDisabled,
        isBaseContentVisible,
        element,
        shotToBeHidden,
        allowToggleVisibility,
        getVisibilityButtonText,
        toggleShow,
        isContentProperty,
    } = useShotItem({ shotElement, isToolbarDisabled, isLoading, isDisabledByReview });
    const { creativeModel } = useDesign();
    const { variations, currentVariation } = useVariations();
    const objectUrl = useObjectUrl(
        element instanceof ImageElement || element instanceof VideoElement ? element?.src : '',
    );
    const previewUrl = useContentPreviewUrl(
        objectUrl || '',
        type,
        element instanceof VideoElement ? element.isAlpha : false,
    );

    const currentPageId = creativeModel.getCurrentPageId();

    const variation = useMemo(() => {
        return variations.find((v) => v.id === currentVariation)?.sizes?.[currentPageId];
    }, [variations, currentVariation, currentPageId]);

    const isDisabledByDeletedAsset = variation?.hasDeletedAsset;

    if (shotToBeHidden || !element) {
        return null;
    }

    const getThumbnail = () => {
        if (isTextElement || isShapeElement || isAudioElement) {
            return (
                <Thumbnail variant="clean" icon={getIcon(type)} iconColor={token.gray400}>
                    {isContentProperty && <Thumbnail.Indicator backgroundColor="teal500" icon={<IconSync />} />}
                </Thumbnail>
            );
        } else if (isImageElement && element instanceof ImageElement) {
            return (
                <Thumbnail variant="content" imageUrl={element.src || shotElement.properties?.value?.thumbnail}>
                    {isDisabledByDeletedAsset && <Thumbnail.Indicator type="error" />}
                    {isContentProperty && <Thumbnail.Indicator backgroundColor="teal500" icon={<IconSync />} />}
                </Thumbnail>
            );
        }

        return (
            <Thumbnail variant="content" imageUrl={previewUrl}>
                {isDisabledByDeletedAsset && <Thumbnail.Indicator type="error" />}
                {isContentProperty && <Thumbnail.Indicator backgroundColor="teal500" icon={<IconSync />} />}
            </Thumbnail>
        );
    };

    return (
        <>
            <List>
                <List.Item
                    thumbnail={getThumbnail()}
                    rightElements={
                        allowToggleVisibility ? (
                            <Button isDisabled={isDisabled} variant="clean" onClick={toggleShow}>
                                {getVisibilityButtonText()}
                            </Button>
                        ) : undefined
                    }
                >
                    {shotElement.name}
                </List.Item>
                {isBaseContentVisible && isTextElement && (
                    <TextElement
                        key={id}
                        elementId={id}
                        elementName={shotElement.name}
                        disabled={isDisabled || isDisabledByReview || isDisabledByDeletedAsset}
                        isLoading={isLoading}
                    />
                )}
                {isBaseContentVisible && !isTextElement && !isShapeElement && (
                    <MediaElement
                        key={id}
                        id={id}
                        type={type as 'image' | 'video' | 'audio'}
                        disabled={isDisabled || isDisabledByReview}
                        isDisabledByDeletedAsset={isDisabledByDeletedAsset}
                        isLoading={isLoading}
                    />
                )}
                {isBaseContentVisible && isShapeElement && (
                    <ShapeElement
                        elementId={shotElement.id}
                        disabled={isDisabled || isDisabledByReview || isDisabledByDeletedAsset}
                    />
                )}
            </List>
            {!isLast && <Inspector.Divider />}
        </>
    );
};

function getIcon(type: ShotElementType) {
    if (type === 'text') {
        return <IconTextField />;
    }

    if (type === 'audio') {
        return <IconMusicLibrary />;
    }

    return <IconBuildShape />;
}

export default ShotItem;
